/* AI HUB (2026-10-27; Jay's idea through Max — managers give the AI their department's instructions and files, everybody
   asks it, wrong answers are corrected, and what the AI "learned" is logged). Four tabs: Ask, Knowledge (per
   department, every change a version), Lessons (corrections; anybody approves — Max: "tất cả"), Activity (the change
   log, the questions, the cost). The rules live on the server (aiHub.js); this screen only shows and sends. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, ThumbsUp, ThumbsDown, Upload, Plus, X, FileText, BookOpen, ChevronDown, Menu, ImagePlus } from 'lucide-react';
import Header from './Header';
import TabIcon from './TabIcon';
import AiTag from './AiTag';
import { useAiTranslate, Tr } from './aiTranslate';
import { Mascot, shrink } from './AskBubble';
import { enterSends, isSmall } from './touchInput';
import { createPortal } from 'react-dom';
import UserAvatar from './UserAvatar';
import { ProposalCard, ReminderPrefs } from './CatchyCard';
import GapReport from './GapReport';
import useAuth from './useAuth';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './ProjectApproval.css';
import './EusuStock.css';
import './AiHub.css';
import ModuleHelp from './ModuleHelp';

/* THE GUIDE (Max 01/11: every module has one). It walks the chat (?tab=ask) — what everybody gets. The Settings tabs are for
   managers and have their own pages in the manual; on them the guide does not open by itself and its button first goes back
   to the chat. Rating buttons and sources only exist after an answer, so they are described in the thread step. */
const TOUR = [
  { selector: '[data-ah-convs]', title: 'tourAiHub.convsT', body: 'tourAiHub.convsB' },
  { selector: '[data-ah-scope]', title: 'tourAiHub.scopeT', body: 'tourAiHub.scopeB' },
  { selector: '[data-ah-thread]', title: 'tourAiHub.threadT', body: 'tourAiHub.threadB' },
  { selector: '[data-ah-input]', title: 'tourAiHub.inputT', body: 'tourAiHub.inputB' },
  { selector: '[data-ah-send]', title: 'tourAiHub.sendT', body: 'tourAiHub.sendB' },
];

const API = `${BACKEND_URL}/api/ai-hub`;
const TABS = ['ask', 'knowledge', 'lessons', 'activity'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const when = (v) => {
  if (!v) return '';
  const d = new Date(String(v).endsWith('Z') || String(v).includes('+') ? v : `${v}Z`);
  if (Number.isNaN(d.getTime())) return String(v);
  const vn = new Date(d.getTime() + 7 * 3600000);
  return `${String(vn.getUTCDate()).padStart(2, '0')}/${MON[vn.getUTCMonth()]}/${vn.getUTCFullYear()} ${vn.toISOString().slice(11, 16)}`;
};
const kb = (n) => (n == null ? '' : n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const usd = (v) => `$${Number(v || 0).toFixed(Number(v || 0) < 1 ? 4 : 2)}`;

const call = async (method, url, body, raw) => {
  const r = await fetch(`${API}${url}`, {
    method, credentials: 'include',
    headers: raw ? { 'Content-Type': 'application/octet-stream' } : body ? { 'Content-Type': 'application/json' } : {},
    body: raw || (body ? JSON.stringify(body) : undefined),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};

/* ---------------------------------------------------------------- the answer's text: bold, lists, tables, [S1] chips */
const Inline = ({ text, onCite, cites }) => String(text).split(/(\*\*[^*]+\*\*|\[(?:S|L)\d+\])/g).map((p, i) => {
  if (!p) return null;
  if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
  const c = p.match(/^\[((?:S|L)\d+)\]$/);
  if (c) {
    const src = cites.find((s) => s.label === c[1]);
    return <button key={i} type="button" className={`ah-cite ${c[1][0] === 'L' ? 'ah-cite-l' : ''}`} title={src ? src.title : c[1]} onClick={() => src && onCite(src)}>{c[1]}</button>;
  }
  // eslint-disable-next-line react/no-array-index-key
  return <React.Fragment key={i}>{p}</React.Fragment>;
});
function Md({ text, onCite, cites }) {
  const lines = String(text || '').split('\n');
  const out = []; let list = null; let table = null;
  const flush = () => {
    if (list) { out.push(list.ordered ? <ol key={`l${out.length}`}>{list.items}</ol> : <ul key={`l${out.length}`}>{list.items}</ul>); list = null; }
    if (table) {
      const rows = table.filter((r) => !/^\s*\|?\s*:?-{2,}/.test(r));
      const cells = rows.map((r) => r.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((x) => x.trim()));
      out.push(
        <div className="ah-tablewrap" key={`t${out.length}`}><table className="ah-mdtable">
          <thead><tr>{(cells[0] || []).map((c, i) => <th key={i}><Inline text={c} onCite={onCite} cites={cites} /></th>)}</tr></thead>
          <tbody>{cells.slice(1).map((r, k) => <tr key={k}>{r.map((c, i) => <td key={i}><Inline text={c} onCite={onCite} cites={cites} /></td>)}</tr>)}</tbody>
        </table></div>,
      );
      table = null;
    }
  };
  lines.forEach((l) => {
    if (/^\s*\|.*\|\s*$/.test(l)) { if (list) flush(); (table = table || []).push(l); return; }
    if (table) flush();
    const b = l.match(/^\s*[-*•]\s+(.*)$/); const n = l.match(/^\s*\d+[.)]\s+(.*)$/);
    if (b || n) {
      const ordered = !!n;
      if (list && list.ordered !== ordered) flush();
      list = list || { ordered, items: [] };
      list.items.push(<li key={list.items.length}><Inline text={(b || n)[1]} onCite={onCite} cites={cites} /></li>);
      return;
    }
    flush();
    if (!l.trim()) return;
    const h = l.match(/^#{1,4}\s+(.*)$/);
    out.push(h ? <h5 key={out.length}><Inline text={h[1]} onCite={onCite} cites={cites} /></h5> : <p key={out.length}><Inline text={l} onCite={onCite} cites={cites} /></p>);
  });
  flush();
  return <div className="ah-md">{out}</div>;
}

/* ---------------------------------------------------------------- a dialog: its own layer above the page */
function Modal({ title, onClose, children, foot, wide }) {
  return (
    <div className="eu-overlay ah-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`eu-modal ah-modal ${wide ? 'ah-wide' : ''}`}>
        <div className="eu-modal-head"><b>{title}</b><button type="button" className="eu-x" onClick={onClose} aria-label="close"><X size={18} /></button></div>
        <div className="eu-modal-body ah-modal-body">{children}</div>
        {foot && <div className="eu-modal-foot">{foot}</div>}
      </div>
    </div>
  );
}

/* the portal's own confirm / name dialog in place of the browser's (Max, 27/10: "cho tôi pop up riêng nha"):
   dlg = { title, body?, input?: true, value?, ok, danger?, run(value) } — run may throw; the message shows in the dialog */
function SmallDialog({ dlg, setDlg }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  if (!dlg) return null;
  const go = async () => {
    if (dlg.input && !String(dlg.value || '').trim()) return;
    setBusy(true);
    try { await dlg.run(String(dlg.value || '').trim()); setDlg(null); } catch (e) { setDlg((x) => ({ ...x, err: e.message })); } finally { setBusy(false); }
  };
  return (
    <Modal title={dlg.title} onClose={() => setDlg(null)} foot={(
      <>
        <button type="button" className="pa-btn" onClick={() => setDlg(null)}>{t('xAiHub.cancel')}</button>
        <button type="button" className={`pa-btn primary${dlg.danger ? ' ah-danger' : ''}`} disabled={busy || (dlg.input && !String(dlg.value || '').trim())} onClick={go} data-ah-dlgok>{dlg.ok}</button>
      </>
    )}>
      {dlg.body && <p className="ah-dlgbody">{dlg.body}</p>}
      {dlg.input && (
        <input className="ah-dlginput" autoFocus value={dlg.value || ''} maxLength={200} data-ah-dlginput
          onChange={(e) => setDlg((x) => ({ ...x, value: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') go(); }} />
      )}
      {dlg.err && <div className="eu-flash bad">{dlg.err}</div>}
    </Modal>
  );
}

/* while the answer is on its way (Max, 27/10: "thêm hiệu ứng để user có cảm giác là AI đang suy nghĩ"): dots, a line that
   moves on every two seconds, shimmering lines where the answer will be, and the seconds waited. The server answers in one
   piece, so the lines are general — they never claim a step the AI may not take. */
function Thinking() {
  const { t } = useTranslation();
  const [n, setN] = useState(0);
  useEffect(() => { const id = setInterval(() => setN((x) => x + 1), 1000); return () => clearInterval(id); }, []);
  const steps = [t('xAiHub.think1'), t('xAiHub.think2'), t('xAiHub.think3')];
  const step = steps[Math.min(Math.floor(n / 2), steps.length - 1)];
  return (
    <div className="ah-a ah-thinking" role="status" aria-live="polite" data-ah-thinking>
      {/* Claude's own spark, turning and breathing the way it does while Claude thinks (Max: "hiệu ứng liên quan đến claude") */}
      <div className="ah-thinkline">
        <span className="ah-sparkwrap" aria-hidden="true"><AiTag size={22} className="ah-spark" /></span>
        <span key={step} className="ah-thinkstep">{step}</span>
        <em>{n}s</em>
      </div>
      <div className="ah-skel" aria-hidden="true"><b style={{ width: '92%' }} /><b style={{ width: '78%' }} /><b style={{ width: '55%' }} /></div>
    </div>
  );
}

/* what people typed, in the reader's portal language — the hook and the mark live in aiTranslate.js since 28/10 (GCC uses
   them too) */

/* ================================================================ Ask */
function AskTab({ teams, onOpenDoc }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useAuth();
  const [scope, setScope] = useState(() => { try { return sessionStorage.getItem('aiHubScope') || ''; } catch (e) { return ''; } });
  const [msgs, setMsgs] = useState([]);
  /* saved conversations (Max, 27/10: "lưu conversation lại để có thể dùng cho lần khác và có thể xóa"): kept on the server
     per person; the open one is remembered for the tab so a reload comes back to it */
  const [convs, setConvs] = useState(null);
  /* ?conv=<pk> first — the bubble's "Open in AI Hub" (29/10, one Catchy) lands on the chat it was having */
  const [convPk, setConvPk] = useState(() => {
    const fromUrl = Number(new URLSearchParams(window.location.search).get('conv')) || null;
    if (fromUrl) return fromUrl;
    try { return Number(sessionStorage.getItem('aiHubConv')) || null; } catch (e) { return null; }
  });
  const loadConvs = useCallback(() => call('GET', '/convs').then((r) => setConvs(r.convs)).catch((e) => setErr(e.message)), []);
  useEffect(() => { loadConvs(); }, [loadConvs]);
  const openConv = useCallback(async (pk) => {
    setErr('');
    if (!pk) { setConvPk(null); setMsgs([]); return; }
    try {
      const r = await call('GET', `/convs/${pk}`);
      setConvPk(pk); setMsgs(r.turns);
      if (r.conv.scope_team !== undefined) setScope(r.conv.scope_team || '');
    } catch (e) { setConvPk(null); setMsgs([]); setErr(e.message); }
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (convPk) openConv(convPk); }, []);
  const [dlg, setDlg] = useState(null);
  const delConv = (c) => setDlg({
    title: t('xAiHub.delete'), body: t('xAiHub.confirmDelConv', { t: c.title }), ok: t('xAiHub.delete'), danger: true,
    run: async () => { await call('DELETE', `/convs/${c.conv_pk}`); if (c.conv_pk === convPk) openConv(null); loadConvs(); },
  });
  const renameConv = (c) => setDlg({
    title: t('xAiHub.renamePrompt'), input: true, value: c.title, ok: t('xAiHub.save'),
    run: async (title) => { if (title !== c.title) { await call('PUT', `/convs/${c.conv_pk}`, { title }); loadConvs(); } },
  });
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [fix, setFix] = useState(null);
  const [src, setSrc] = useState(null);
  const endRef = useRef(null);
  useEffect(() => { try { sessionStorage.setItem('aiHubConv', convPk ? String(convPk) : ''); sessionStorage.setItem('aiHubScope', scope); } catch (e) { /* memory only */ } }, [convPk, scope]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }); }, [msgs, busy]);
  const nameOf = (k) => teams.find((x) => x.key === k)?.name || k;

  /* A PHOTO WITH THE QUESTION (Jay task #1172: "send a photo straight from the phone camera") — the floating window's way: made
     small on the phone (1600px JPEG), sent once with the question, shown as a thumbnail in it */
  const [photo, setPhoto] = useState(null);
  const photoRef = useRef(null);
  const pickPhoto = async (file) => { if (file && /^image\//.test(file.type)) { try { setPhoto(await shrink(file)); } catch (e) { /* not a picture */ } } };
  const ask = async () => {
    const question = q.trim() || (photo ? t('xAiHub.photoAsk') : ''); if (!question || busy) return;
    const pic = photo;
    setErr(''); setBusy(true); setQ(''); setPhoto(null);
    setMsgs((m) => [...m, { q: question, scope, pending: true, askedAt: new Date().toISOString(), thumb: pic?.thumb }]);
    try {
      const r = await call('POST', '/ask', { question, team: scope || null, convPk, ...(pic ? { image: { mediaType: pic.mediaType, data: pic.data } } : {}) });
      setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { q: question, scope, thumb: x.thumb, a: r.answer, sources: r.sources, askPk: r.askPk, gap: r.gap || null, unverified: r.unverified || [], lookups: r.lookups || [], lesson: r.lesson || null, proposals: r.proposals || [], askedAt: r.askedAt || x.askedAt, at: r.at } : x)));
      if (r.convPk && r.convPk !== convPk) setConvPk(r.convPk);
      loadConvs();
    } catch (e) {
      setErr(e.message); setMsgs((m) => m.slice(0, -1)); setQ(question); setPhoto(pic);
    } finally { setBusy(false); }
  };
  /* a proposal card confirmed / cancelled under answer i (Catchy editing the trackers, 30/10) */
  const cardChanged = (i, card) => setMsgs((all) => all.map((x, k) => (k === i ? { ...x, proposals: (x.proposals || []).map((c) => (c.id === card.id ? card : c)) } : x)));
  const rate = async (i, value, note) => {
    const m = msgs[i];
    await call('POST', `/asks/${m.askPk}/feedback`, { value, note });
    setMsgs((all) => all.map((x, k) => (k === i ? { ...x, rated: value } : x)));
  };
  const sendFix = async () => {
    const m = msgs[fix.i];
    try {
      await rate(fix.i, -1, fix.note);
      if (fix.answer.trim()) {
        await call('POST', '/lessons', { teams: fix.teams, question: m.q, answer: fix.answer, askPk: m.askPk });
        setMsgs((all) => all.map((x, k) => (k === fix.i ? { ...x, proposed: true } : x)));
      }
      setFix(null);
    } catch (e) { setFix((f) => ({ ...f, err: e.message })); }
  };
  /* a correction typed in the chat came back as a proposed lesson: approve it here, or drop it (anybody may — Max) */
  const decideCaptured = async (i, decision) => {
    const m = msgs[i];
    try {
      await call('POST', `/lessons/${m.lesson.lessonPk}/decide`, { decision, note: 'from the chat' });
      setMsgs((all) => all.map((x, k) => (k === i ? { ...x, lesson: { ...x.lesson, status: decision === 'approve' ? 'approved' : 'rejected' } } : x)));
    } catch (e) { setErr(e.message); }
  };
  const justRate = async () => {
    try { await rate(fix.i, -1, fix.note); setFix(null); } catch (e) { setFix((f) => ({ ...f, err: e.message })); }
  };
  const openFix = (i) => {
    const m = msgs[i];
    const team = m.scope || (m.sources || []).find((s) => s.cited && s.team)?.team || (m.sources || [])[0]?.team || teams[0]?.key || '';
    setFix({ i, teams: team ? [team] : [], note: '', answer: '' });
  };

  /* ON A PHONE (Jay task #1172) the conversations are a menu over the chat, not a column above it — the chat takes the screen */
  const [convMenu, setConvMenu] = useState(false);
  return (
    <div className="ah-askwrap">
    <div className="ah-mobbar" data-ah-mobbar>
      <button type="button" className="pa-btn" onClick={() => setConvMenu(true)} data-ah-convmenu><Menu size={15} /> {t('xAiHub.convs')} ({(convs || []).length})</button>
      <button type="button" className="pa-btn primary" onClick={() => openConv(null)}><Plus size={14} /> {t('xAiHub.newChat')}</button>
    </div>
    {(() => {
      const list = (
    <aside className={`ah-convs${convMenu ? ' open' : ''}`} data-ah-convs onClick={(e) => { if (convMenu && e.target.closest('.ah-convopen, .ah-newconv')) setConvMenu(false); }}>
          <button type="button" className="ah-convclose" onClick={() => setConvMenu(false)} aria-label={t('xAiHub.cancel')} data-ah-convclose><X size={18} /></button>
          <button type="button" className="pa-btn primary ah-newconv" onClick={() => openConv(null)} data-ah-newconv><Plus size={14} /> {t('xAiHub.newChat')}</button>
          <h4>{t('xAiHub.convs')}</h4>
          {convs && !convs.length && <p className="ah-muted">{t('xAiHub.noConvs')}</p>}
          {(convs || []).map((c) => (
            <div key={c.conv_pk} className={`ah-conv ${c.conv_pk === convPk ? 'on' : ''}`} data-ah-conv={c.conv_pk}>
              <button type="button" className="ah-convopen" onClick={() => openConv(c.conv_pk)} title={c.title}>
                <b>{c.title}</b>
                <span>{when(c.updated_at)} · {t('xAiHub.turns', { n: c.turns })}{c.source === 'bubble' && <em className="ah-src" data-ah-src="bubble"> · {t('xAiHub.fromBubble')}</em>}{c.source === 'catchy' && <em className="ah-src ah-src-catchy" data-ah-src="catchy"> · {t('xCatchy.startedByCatchy')}</em>}</span>
              </button>
              <div className="ah-convbtns">
                <button type="button" className="pa-btn ah-b-edit" onClick={() => renameConv(c)}>{t('xAiHub.rename')}</button>
                <button type="button" className="pa-btn ah-b-off" onClick={() => delConv(c)} data-ah-delconv>{t('xAiHub.delete')}</button>
              </div>
            </div>
          ))}
        </aside>
      );
      // phones: the open menu on the body, above everything (iOS put it off-screen from inside the page — Max 04/11)
      return convMenu ? createPortal(list, document.body) : list;
    })()}
    <div className="ah-ask">
      <div className="ah-askbar">
        <label className="iv-field"><span>{t('xAiHub.scope')}</span>
          <select value={scope} onChange={(e) => setScope(e.target.value)} data-ah-scope>
            <option value="">{t('xAiHub.allDepts')}</option>
            {teams.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}
          </select>
        </label>
        <ReminderPrefs />
      </div>
      <div className="ah-thread" data-ah-thread>
        {!msgs.length && <div className="ah-empty"><BookOpen size={22} /><span>{t('xAiHub.emptyAsk')}</span></div>}
        {msgs.map((m, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="ah-turn">
            {/* the asker's own face beside the question (Jay, 27/10) — a conversation is only ever its owner's */}
            {/* a reminder Catchy started has no question of the person's (catchyWrite.js) — no empty bubble for it */}
            {m.startedBy !== 'catchy' && <div className="ah-mine"><div className="ah-q">{m.thumb && <img className="ah-qthumb" src={m.thumb} alt="" />}<span>{m.q}</span>{m.scope && <em>{nameOf(m.scope)}</em>}</div><UserAvatar url={me.avatarUrl} name={me.name} size={30} /></div>}
            {/* the time under each message (Jay, 27/10); turns saved before 27/10 evening kept only the answer's */}
            {m.askedAt && m.startedBy !== 'catchy' && <div className="ah-time ah-time-q">{when(m.askedAt)}</div>}
            {m.pending ? <Thinking /> : (
              <div className="ah-a" data-ah-answer>
                <div className="ah-who"><Mascot className="ah-catchy" /> Catchy</div>
                <Md text={m.a} cites={m.sources || []} onCite={setSrc} />
                {(m.sources || []).some((s) => s.kind === 'portal') && (
                  <div className="ah-sources ah-portal">
                    <span>{t('xAiHub.fromPortal')}:</span>
                    {(m.sources || []).filter((s) => s.kind === 'portal').map((s) => <span key={s.title} className="ah-portalchip">{s.title}</span>)}
                  </div>
                )}
                {(m.sources || []).some((s) => s.cited && s.kind !== 'portal') && (
                  <div className="ah-sources">
                    <span>{t('xAiHub.sources')}:</span>
                    {(m.sources || []).filter((s) => s.cited && s.kind !== 'portal').map((s) => (
                      <button key={s.label} type="button" className="ah-srcchip" onClick={() => setSrc(s)}>
                        <b>{s.label}</b> {s.kind === 'lesson' ? `${t('xAiHub.lesson')} · ` : ''}{s.title}{s.version ? ` v${s.version}` : ''} <em>{s.teamName}</em>
                      </button>
                    ))}
                  </div>
                )}
                {(m.proposals || []).map((c) => <ProposalCard key={c.id} card={c} onChange={(nc) => cardChanged(i, nc)} />)}
                {/* what blocked the answer, and a button to tell the admin (Max 31/10) */}
                {!m.pending && <GapReport askPk={m.askPk} gap={m.gap} unverified={m.unverified || []} lookups={m.lookups || []} gaveUp={String(m.a || '').startsWith('I could not finish looking this up')} />}
                {m.lesson && (
                  <div className={`ah-captured ${m.lesson.status || ''}`} data-ah-captured>
                    <b>📝 {m.lesson.status === 'approved' ? t('xAiHub.capturedApproved') : m.lesson.status === 'rejected' ? t('xAiHub.capturedDropped') : t('xAiHub.captured', { d: m.lesson.teamName || m.lesson.team })}</b>
                    <div className="ah-capq">{m.lesson.question}</div>
                    <div className="ah-capa">{m.lesson.answer}</div>
                    {!m.lesson.status && (
                      <div className="ah-capbtns">
                        <button type="button" className="pa-btn ah-b-ok" onClick={() => decideCaptured(i, 'approve')} data-ah-capapprove>{t('xAiHub.approveNow')}</button>
                        <button type="button" className="pa-btn ah-b-off" onClick={() => decideCaptured(i, 'reject')}>{t('xAiHub.dropIt')}</button>
                      </div>
                    )}
                  </div>
                )}
                <div className="ah-rate">
                  {m.rated ? <span className="ah-rated">{m.proposed ? t('xAiHub.proposed') : t('xAiHub.rated')}</span> : (
                    <>
                      {/* icons only, in colour (Jay, 27/10: "No need letters. Just icons. 👍👎 w/ color"); the words stay as the tooltip and the accessible name */}
                      <button type="button" className="ah-rateicon ah-up" title={t('xAiHub.good')} aria-label={t('xAiHub.good')} onClick={() => rate(i, 1).catch((e) => setErr(e.message))} data-ah-good><ThumbsUp size={18} /></button>
                      <button type="button" className="ah-rateicon ah-down" title={t('xAiHub.bad')} aria-label={t('xAiHub.bad')} onClick={() => openFix(i)} data-ah-bad><ThumbsDown size={18} /></button>
                    </>
                  )}
                </div>
              </div>
            )}
            {!m.pending && m.at && <div className="ah-time">{when(m.at)}</div>}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      {err && <div className="eu-flash bad">{err}</div>}
      {photo && (
        <div className="ah-photo" data-ah-photo><img src={photo.thumb} alt="" /><button type="button" onClick={() => setPhoto(null)} aria-label={t('xAiHub.cancel')}><X size={14} /></button></div>
      )}
      <div className="ah-compose">
        <button type="button" className="pa-btn ah-photobtn" title={t('xAiHub.photo')} aria-label={t('xAiHub.photo')} onClick={() => photoRef.current?.click()} data-ah-photobtn><ImagePlus size={17} /></button>
        <input ref={photoRef} type="file" accept="image/*" hidden onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ''; }} data-ah-photoinput />
        <textarea value={q} rows={2} placeholder={isSmall() ? t('xAiHub.placeholderTouch') : t('xAiHub.placeholder')} onChange={(e) => setQ(e.target.value)} data-ah-input
          onPaste={(e) => { const f = [...(e.clipboardData?.items || [])].find((it) => it.kind === 'file' && /^image\//.test(it.type)); if (f) { e.preventDefault(); pickPhoto(f.getAsFile()); } }}
          onKeyDown={(e) => { if (enterSends(e)) { e.preventDefault(); ask(); } }} />
        <button type="button" className="pa-btn primary ah-send" disabled={busy || (!q.trim() && !photo)} onClick={ask} data-ah-send><Send size={15} /> {t('xAiHub.send')}</button>
      </div>
      {/* the DAC VN Dashboard's line (Jay, task #1169) — the floating window carries it under Catchy's name */}
      <div className="ah-notice" data-catchy-notice>{t('ask.note')}</div>
    </div>

      <SmallDialog dlg={dlg} setDlg={setDlg} />
      {src && (
        <Modal title={`${src.label || ''} ${src.title}`} onClose={() => setSrc(null)} foot={src.docCode ? (
          /* a passage from an E-Docs document switched on for Catchy (task #105) opens that document in E-Docs */
          <button type="button" className="pa-btn" onClick={() => { const s = src; setSrc(null); navigate(`/documents/${s.docCode}`); }}><FileText size={14} /> {t('xAiHub.actView')}</button>
        ) : src.docPk ? (
          <button type="button" className="pa-btn" onClick={() => { const s = src; setSrc(null); onOpenDoc(s.team, s.docPk); }}><FileText size={14} /> {t('xAiHub.actView')}</button>
        ) : null}>
          <p className="eu-sub">{src.teamName}{src.version ? ` · v${src.version}` : ''}{src.part ? ` · ${t('xAiHub.part', { n: src.part })}` : ''}{src.approvedBy ? ` · ${t('xAiHub.approvedBy', { n: src.approvedBy })}` : ''}</p>
          <pre className="ah-excerpt">{src.excerpt}</pre>
        </Modal>
      )}
      {fix && (
        <Modal title={t('xAiHub.fixTitle')} onClose={() => setFix(null)} foot={(
          <>
            <button type="button" className="pa-btn" onClick={() => setFix(null)}>{t('xAiHub.cancel')}</button>
            <button type="button" className="pa-btn" onClick={justRate} data-ah-justrate>{t('xAiHub.justRate')}</button>
            <button type="button" className="pa-btn primary" disabled={!fix.answer.trim() || !fix.teams.length} onClick={sendFix} data-ah-fixsend>{t('xAiHub.fixSend')}</button>
          </>
        )}>
          <p className="eu-sub">{t('xAiHub.fixSub')}</p>
          <label className="ah-field"><span>{t('xAiHub.fixNote')}</span><input value={fix.note} onChange={(e) => setFix((f) => ({ ...f, note: e.target.value }))} /></label>
          <label className="ah-field"><span>{t('xAiHub.fixAnswer')}</span><textarea rows={5} value={fix.answer} onChange={(e) => setFix((f) => ({ ...f, answer: e.target.value }))} data-ah-fixanswer /></label>
          <DeptChecks teams={teams} value={fix.teams} onChange={(v) => setFix((f) => ({ ...f, teams: v }))} />
          {fix.err && <div className="eu-flash bad">{fix.err}</div>}
        </Modal>
      )}
    </div>
  );
}

/* A lesson's departments — a dropdown that takes several (Jay, 29/10: "Please allow multiple check-box. In many cases,
   1 problem belongs to many departments"; Max, same day: a dropdown, not the whole list open in the dialog). The chosen ones
   sit in the box as chips; the list opens under it with a search. Its panel is FIXED to the screen, above the dialog's
   layer: inside the dialog's scrolling body it would be cut off. The first chosen, in the list's order, files the lesson. */
/* GENERAL (Jay, 30/10): the key '*' = every department, also ones added later (aiHub.js GENERAL). It sits at the top of the
   list and stands alone: ticking it clears and locks the others. */
export const GENERAL = '*';
function DeptChecks({ teams, value, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState(null);
  const boxRef = useRef(null); const panelRef = useRef(null);
  const on = new Set(value);
  const general = on.has(GENERAL);
  const flip = (k) => {
    if (k === GENERAL) { onChange(general ? [] : [GENERAL]); return; }
    if (general) return;
    onChange(teams.map((x) => x.key).filter((x) => (x === k ? !on.has(x) : on.has(x))));
  };
  const place = useCallback(() => {
    const r = boxRef.current?.getBoundingClientRect();
    if (!r) return;
    const room = window.innerHeight - r.bottom;
    setPos(room < 280 && r.top > room ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4 } : { left: r.left, width: r.width, top: r.bottom + 4 });
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    place();
    const away = (e) => { if (!boxRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', away); document.addEventListener('keydown', esc, true);
    window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc, true);
      window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);
  const shown = teams.filter((x) => !q.trim() || x.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="ah-field ah-deptfield">
      <span>{t('xAiHub.fixDept')} <em className="ah-muted">· {t('xAiHub.deptsHint')}</em></span>
      <div ref={boxRef} className={`ah-ms ${open ? 'open' : ''}`} role="button" tabIndex={0} aria-haspopup="listbox" aria-expanded={open}
        onClick={() => { setOpen((o) => !o); setQ(''); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); } }}
        data-ah-depts>
        <div className="ah-ms-chips">
          {general ? (
            <span className="ah-ms-chip ah-ms-chip-general" data-ah-chip={GENERAL}>{t('xAiHub.general')}
              <button type="button" aria-label={`${t('xAiHub.cancel')} ${t('xAiHub.general')}`} onClick={(e) => { e.stopPropagation(); flip(GENERAL); }}><X size={12} /></button>
            </span>
          ) : value.length ? teams.filter((x) => on.has(x.key)).map((x) => (
            <span key={x.key} className="ah-ms-chip" data-ah-chip={x.key}>{x.name}
              <button type="button" aria-label={`${t('xAiHub.cancel')} ${x.name}`} onClick={(e) => { e.stopPropagation(); flip(x.key); }}><X size={12} /></button>
            </span>
          )) : <span className="ah-muted">{t('xAiHub.deptsPick')}</span>}
        </div>
        <ChevronDown size={16} className="ah-ms-caret" />
      </div>
      {open && pos && (
        <div ref={panelRef} className="ah-ms-panel" style={pos} role="listbox" aria-multiselectable="true" data-ah-deptpanel>
          <input className="ah-ms-search" autoFocus value={q} placeholder={t('xAiHub.deptsSearch')} onChange={(e) => setQ(e.target.value)} />
          <div className="ah-ms-list">
            {!q.trim() && (
              <label className={`ah-ms-general${general ? ' on' : ''}`} title={t('xAiHub.generalHint')}>
                <input type="checkbox" checked={general} onChange={() => flip(GENERAL)} data-ah-dept={GENERAL} /> <b>{t('xAiHub.general')}</b>
                <span className="ah-muted"> · {t('xAiHub.generalHint')}</span>
              </label>
            )}
            {shown.map((x) => (
              <label key={x.key} className={`${on.has(x.key) ? 'on' : ''}${general ? ' off' : ''}`}>
                <input type="checkbox" checked={on.has(x.key)} disabled={general} onChange={() => flip(x.key)} data-ah-dept={x.key} /> {x.name}
              </label>
            ))}
            {!shown.length && <p className="ah-muted ah-ms-none">—</p>}
          </div>
          <div className="ah-ms-foot">
            <span className="ah-muted">{general ? t('xAiHub.general') : t('xAiHub.deptsChosen', { n: value.length })}</span>
            <button type="button" className="pa-btn" onClick={() => setOpen(false)} data-ah-deptdone>{t('xAiHub.deptsDone')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================ Knowledge */
function KnowledgeTab({ teams, reloadTeams, focus }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  /* the department's description is written once, in English, in Departments — read in the portal's language (Max, 27/10) */
  const { tr } = useAiTranslate(useMemo(() => teams.map((x) => x.description).filter(Boolean), [teams]));
  const [team, setTeam] = useState(focus?.team || teams[0]?.key || '');
  const [docs, setDocs] = useState(null);
  const [err, setErr] = useState('');
  const [flash, setFlash] = useState('');
  const [instr, setInstr] = useState(null);   // { docPk?, title, text, note }
  const [upload, setUpload] = useState(null); // { docPk?, title, note, file }
  const [hist, setHist] = useState(null);     // { doc, versions, text }
  const [busy, setBusy] = useState(false);
  const cur = teams.find((x) => x.key === team);
  const load = useCallback(async () => {
    if (!team) return;
    setErr('');
    try { setDocs((await call('GET', `/docs?team=${team}`)).docs); } catch (e) { setErr(e.message); }
  }, [team]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (focus?.team) setTeam(focus.team); }, [focus]);
  const openHist = async (docPk) => { try { setHist(await call('GET', `/docs/${docPk}`)); } catch (e) { setErr(e.message); } };
  useEffect(() => { if (focus?.docPk) openHist(focus.docPk); }, [focus]);
  const done = (msg) => { setFlash(msg); load(); reloadTeams(); };

  const saveInstr = async () => {
    setBusy(true);
    try {
      const r = await call('POST', '/instructions', { team, docPk: instr.docPk || null, title: instr.title, text: instr.text, note: instr.note, active: instr.active !== false });
      setInstr(null); done(t('xAiHub.uploaded', { v: r.versionNo, p: r.passages }));
    } catch (e) { setInstr((x) => ({ ...x, err: e.message })); } finally { setBusy(false); }
  };
  const sendFile = async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams({ file: upload.file.name, note: upload.note || '', title: upload.title || '', active: upload.active === false ? '0' : '1' });
      if (upload.docPk) qs.set('doc', upload.docPk); else qs.set('team', team);
      const r = await call('PUT', `/files?${qs}`, null, upload.file);
      setUpload(null); done(t('xAiHub.uploaded', { v: r.versionNo, p: r.passages }));
    } catch (e) { setUpload((x) => ({ ...x, err: e.message })); } finally { setBusy(false); }
  };
  const [dlg, setDlg] = useState(null);
  const setActive = async (d, active) => {
    if (active) { try { await call('POST', `/docs/${d.doc_pk}/active`, { active }); done(''); } catch (e) { setErr(e.message); } return; }
    setDlg({ title: t('xAiHub.turnOff'), body: t('xAiHub.confirmRetire', { t: d.title }), ok: t('xAiHub.turnOff'), danger: true,
      run: async () => { await call('POST', `/docs/${d.doc_pk}/active`, { active: false }); done(''); } });
  };
  const rollback = (v) => setDlg({ title: t('xAiHub.actRollback'), body: t('xAiHub.confirmRollback', { v: v.version_no }), ok: t('xAiHub.actRollback'),
    run: async () => { await call('POST', `/docs/${hist.doc.doc_pk}/rollback`, { verPk: v.ver_pk }); setHist(null); done(''); } });
  const editInstr = async (d) => {
    try { const x = await call('GET', `/docs/${d.doc_pk}`); setInstr({ docPk: d.doc_pk, title: d.title, text: x.text, note: '', active: !!d.is_active }); } catch (e) { setErr(e.message); }
  };

  const list = (kind) => (docs || []).filter((d) => d.kind === kind);
  const table = (kind) => (
    <table className="pa-table ah-table" data-ah-docs={kind}>
      <thead><tr>
        <th>{t('xAiHub.colTitle')}</th><th>{t('xAiHub.colVersion')}</th><th>{t('xAiHub.colUpdated')}</th><th>{t('xAiHub.colBy')}</th>
        {kind === 'file' && <th>{t('xAiHub.colSize')}</th>}{kind === 'file' && <th className="num">{t('xAiHub.colPassages')}</th>}
        <th>{t('xAiHub.colNote')}</th><th>{t('xAiHub.colActive')}</th><th>{t('xAiHub.colAction')}</th>
      </tr></thead>
      <tbody>
        {list(kind).map((d) => (
          <tr key={d.doc_pk} className={d.is_active ? '' : 'ah-off'}>
            <td><b>{d.title}</b>{kind === 'file' && d.file_name && <div className="ah-muted">{d.file_name}</div>}
              {/* moved to E-Docs (task #105): Catchy reads the E-Docs copy; this row stays for its history */}
              {!!d.moved_to && (
                <div className="ah-muted" data-ah-moved={d.doc_pk}>
                  {t('xAiHub.movedTo', { code: d.moved_to })}{' '}
                  <button type="button" className="pa-btn ah-b-view" onClick={() => navigate(`/documents/${d.moved_to}`)}>{t('xAiHub.actView')}</button>
                </div>
              )}
            </td>
            <td>v{d.version_no}</td><td>{when(d.ver_at)}</td><td>{d.ver_by}</td>
            {kind === 'file' && <td>{kb(d.bytes)}</td>}{kind === 'file' && <td className="num">{d.passages}</td>}
            <td className="ah-note">{d.note}</td>
            {/* whether Catchy reads this document (Jay, 27/10: "Add an 'Active' toggle for each document") — a switch for the
                department's managers, a plain On / Off for everybody else */}
            <td>
              {cur?.canManage ? (
                <button type="button" role="switch" aria-checked={!!d.is_active} className={`ah-switch ${d.is_active ? 'on' : ''}`}
                  title={d.is_active ? t('xAiHub.activeOnHint') : t('xAiHub.activeOffHint')} onClick={() => setActive(d, !d.is_active)} data-ah-active={d.doc_pk}>
                  <i /><span>{d.is_active ? t('xAiHub.activeOn') : t('xAiHub.activeOff')}</span>
                </button>
              ) : <span className={`ah-st ${d.is_active ? 'ah-st-approved' : 'ah-st-retired'}`}>{d.is_active ? t('xAiHub.activeOn') : t('xAiHub.activeOff')}</span>}
            </td>
            <td className="ah-actions">
              <button type="button" className="pa-btn ah-b-view" onClick={() => openHist(d.doc_pk)}>{t('xAiHub.actHistory')}</button>
              {kind === 'file' && <a className="pa-btn ah-b-view" href={`${API}/versions/${d.ver_pk}/file`}>{t('xAiHub.actDownload')}</a>}
              {cur?.canManage && kind === 'instruction' && <button type="button" className="pa-btn ah-b-edit" onClick={() => editInstr(d)}>{t('xAiHub.actEdit')}</button>}
              {cur?.canManage && kind === 'file' && <button type="button" className="pa-btn ah-b-edit" onClick={() => setUpload({ docPk: d.doc_pk, title: d.title, note: '', file: null, active: !!d.is_active })}>{t('xAiHub.actNewVersion')}</button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="ah-know">
      <aside className="ah-depts" data-ah-depts>
        <h4>{t('xAiHub.depts')}</h4>
        {teams.map((x) => (
          <button key={x.key} type="button" className={`ah-dept ${x.key === team ? 'on' : ''}`} onClick={() => { setTeam(x.key); setFlash(''); }}>
            <b>{x.name}</b>
            <span>{t('xAiHub.counts', { i: x.instructions, f: x.files, l: x.lessons })}</span>
            {x.canManage && <em>{t('xAiHub.youManage')}</em>}
          </button>
        ))}
      </aside>
      <section className="ah-docs">
        <div className="ah-docshead">
          <div><h3>{cur?.name}</h3>{cur?.description && <p className="eu-sub" data-ah-teamdesc><Tr text={cur.description} tr={tr} /></p>}</div>
          {cur?.canManage ? (
            <div className="ah-docbtns">
              <button type="button" className="pa-btn" onClick={() => setInstr({ title: '', text: '', note: '' })} data-ah-newinstr><Plus size={14} /> {t('xAiHub.newInstr')}</button>
              <button type="button" className="pa-btn primary" onClick={() => setUpload({ title: '', note: '', file: null })} data-ah-upload><Upload size={14} /> {t('xAiHub.uploadFile')}</button>
            </div>
          ) : <span className="ah-muted">{t('xAiHub.readOnly')}</span>}
        </div>
        {flash && <div className="eu-flash ok">{flash}</div>}
        {err && <div className="eu-flash bad">{err}</div>}
        {!docs ? <p className="ah-muted">{t('xAiHub.loading')}</p> : (
          <>
            <h4 className="ah-h">{t('xAiHub.instructions')}</h4>
            {list('instruction').length ? table('instruction') : <p className="ah-muted">{t('xAiHub.noInstr')}</p>}
            <h4 className="ah-h">{t('xAiHub.files')}</h4>
            {list('file').length ? table('file') : <p className="ah-muted">{t('xAiHub.noFiles')}</p>}
          </>
        )}
      </section>

      {instr && (
        <Modal wide title={instr.docPk ? `${t('xAiHub.actEdit')} — ${instr.title}` : t('xAiHub.newInstr')} onClose={() => setInstr(null)} foot={(
          <>
            <button type="button" className="pa-btn" onClick={() => setInstr(null)}>{t('xAiHub.cancel')}</button>
            <button type="button" className="pa-btn primary" disabled={busy || !instr.text.trim()} onClick={saveInstr} data-ah-saveinstr>{t('xAiHub.save')}</button>
          </>
        )}>
          <p className="eu-sub">{t('xAiHub.instrHint')}</p>
          <label className="ah-field"><span>{t('xAiHub.instrTitle')}</span><input value={instr.title} onChange={(e) => setInstr((x) => ({ ...x, title: e.target.value }))} data-ah-instrtitle /></label>
          <label className="ah-field"><span>{t('xAiHub.instrText')}</span><textarea rows={12} value={instr.text} onChange={(e) => setInstr((x) => ({ ...x, text: e.target.value }))} data-ah-instrtext /></label>
          <label className="ah-field"><span>{t('xAiHub.instrNote')}</span><input value={instr.note} onChange={(e) => setInstr((x) => ({ ...x, note: e.target.value }))} /></label>
          <label className="ah-check"><input type="checkbox" checked={instr.active !== false} onChange={(e) => setInstr((x) => ({ ...x, active: e.target.checked }))} data-ah-usenow /> {t('xAiHub.useNow')}</label>
          {instr.err && <div className="eu-flash bad">{instr.err}</div>}
        </Modal>
      )}
      {upload && (
        <Modal title={upload.docPk ? `${t('xAiHub.actNewVersion')} — ${upload.title}` : t('xAiHub.uploadFile')} onClose={() => setUpload(null)} foot={(
          <>
            <button type="button" className="pa-btn" onClick={() => setUpload(null)}>{t('xAiHub.cancel')}</button>
            <button type="button" className="pa-btn primary" disabled={busy || !upload.file} onClick={sendFile} data-ah-sendfile>{busy ? t('xAiHub.uploading') : t('xAiHub.save')}</button>
          </>
        )}>
          <p className="eu-sub">{t('xAiHub.fileHint')}</p>
          <label className="ah-field"><span>{t('xAiHub.chooseFile')}</span>
            <input type="file" accept=".pdf,.docx,.pptx,.xlsx,.xlsm,.txt,.md,.csv,.json,.html,.htm" data-ah-file
              onChange={(e) => { const f = e.target.files?.[0] || null; setUpload((x) => ({ ...x, file: f, title: x.title || (f ? f.name.replace(/\.[^.]+$/, '') : '') })); }} />
          </label>
          <label className="ah-field"><span>{t('xAiHub.fileTitle')}</span><input value={upload.title} onChange={(e) => setUpload((x) => ({ ...x, title: e.target.value }))} /></label>
          <label className="ah-field"><span>{t('xAiHub.instrNote')}</span><input value={upload.note} onChange={(e) => setUpload((x) => ({ ...x, note: e.target.value }))} /></label>
          <label className="ah-check"><input type="checkbox" checked={upload.active !== false} onChange={(e) => setUpload((x) => ({ ...x, active: e.target.checked }))} data-ah-usenow /> {t('xAiHub.useNow')}</label>
          {upload.err && <div className="eu-flash bad">{upload.err}</div>}
        </Modal>
      )}
      {hist && (
        <Modal wide title={t('xAiHub.historyTitle', { t: hist.doc.title })} onClose={() => setHist(null)}>
          <table className="pa-table ah-table">
            <thead><tr><th>{t('xAiHub.colVersion')}</th><th>{t('xAiHub.colUpdated')}</th><th>{t('xAiHub.colBy')}</th><th>{t('xAiHub.colNote')}</th><th>{t('xAiHub.colAction')}</th></tr></thead>
            <tbody>
              {hist.versions.map((v) => (
                <tr key={v.ver_pk}>
                  <td>v{v.version_no}{v.ver_pk === hist.doc.current_ver_pk && <span className="ah-tag ah-tag-on">{t('xAiHub.current')}</span>}</td>
                  <td>{when(v.created_at)}</td><td>{v.created_by}</td><td className="ah-note">{v.note}</td>
                  <td className="ah-actions">
                    {v.file_name && <a className="pa-btn ah-b-view" href={`${API}/versions/${v.ver_pk}/file`}>{t('xAiHub.actDownload')}</a>}
                    {cur?.canManage && v.ver_pk !== hist.doc.current_ver_pk && <button type="button" className="pa-btn ah-b-edit" onClick={() => rollback(v)}>{t('xAiHub.actRollback')}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <h4 className="ah-h">{t('xAiHub.viewText')} (v{hist.versions.find((v) => v.ver_pk === hist.doc.current_ver_pk)?.version_no})</h4>
          <pre className="ah-excerpt ah-fulltext">{hist.text}</pre>
        </Modal>
      )}
      {/* after the history dialog, so a rollback asked from inside it opens on top of it */}
      <SmallDialog dlg={dlg} setDlg={setDlg} />
    </div>
  );
}

/* ================================================================ Lessons */
/* sort by a column and search the rows (Max, 27/10: "thêm sort và filter nhé"): a click on a heading sorts by it, a second
   click turns the order round; the search ignores accents ("gia" finds "giá") and looks through everything a row shows */
const fold = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').normalize('NFC').replace(/\u0111/gi, 'd').toLowerCase();
function useTableView(rows, cols, textOf) {
  const [sort, setSort] = useState({ k: null, dir: 1 });
  const [qs, setQs] = useState('');
  const view = useMemo(() => {
    let out = rows || [];
    const needle = fold(qs.trim());
    if (needle) out = out.filter((r) => fold(textOf(r)).includes(needle));
    const f = sort.k && cols[sort.k];
    if (f) {
      out = [...out].sort((a, b) => {
        const x = f(a); const y = f(b);
        const ex = x === null || x === undefined || x === ''; const ey = y === null || y === undefined || y === '';
        if (ex || ey) return ex && ey ? 0 : ex ? 1 : -1;
        return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true })) * sort.dir;
      });
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, qs, sort]);
  const th = (k, label, className = '') => (
    <th key={k} className={`ah-sortth ${className}`.trim()} aria-sort={sort.k === k ? (sort.dir > 0 ? 'ascending' : 'descending') : 'none'}
      onClick={() => setSort((x) => ({ k, dir: x.k === k ? -x.dir : 1 }))} data-ah-sort={k}>
      {label}<span className="ah-arrow">{sort.k === k ? (sort.dir > 0 ? '▲' : '▼') : '↕'}</span>
    </th>
  );
  return { view, th, qs, setQs };
}
function SearchBox({ value, onChange, shown, total }) {
  const { t } = useTranslation();
  return (
    <span className="ah-searchbox">
      <input className="ah-search" type="search" value={value} placeholder={t('xAiHub.search')} onChange={(e) => onChange(e.target.value)} data-ah-search />
      {total > 0 && <em>{t('xAiHub.shown', { n: shown, total })}</em>}
    </span>
  );
}

function LessonsTab({ teams, reloadTeams, admin }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('proposed');
  const [team, setTeam] = useState('');
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [decide, setDecide] = useState(null);
  const [neu, setNeu] = useState(null);
  const nameOf = (k) => (k === GENERAL ? t('xAiHub.general') : teams.find((x) => x.key === k)?.name || k);
  const isGen = (l) => (l.teams || []).includes(GENERAL) || l.team_key === GENERAL;
  // a General lesson is approved or retired by an Admin only (the server says the same)
  const mayDecide = (l) => admin || !isGen(l);
  const deptsOf = (l) => (l.teams?.length ? l.teams : [l.team_key]).map(nameOf).join(', ');
  const load = useCallback(async () => {
    setErr('');
    try { setRows((await call('GET', `/lessons?${new URLSearchParams({ ...(status ? { status } : {}), ...(team ? { team } : {}) })}`)).lessons); } catch (e) { setErr(e.message); }
  }, [status, team]);
  useEffect(() => { load(); }, [load]);
  const verb = { approve: t('xAiHub.actApprove'), reject: t('xAiHub.actReject'), retire: t('xAiHub.actRetireLesson') };
  const [orig, setOrig] = useState(false);
  const { tr, err: trErr } = useAiTranslate(useMemo(() => (rows || []).flatMap((l) => [l.question, l.answer, l.ask_answer, l.decision_note]), [rows]));
  const doDecide = async () => {
    try { await call('POST', `/lessons/${decide.l.lesson_pk}/decide`, { decision: decide.d, note: decide.note }); setDecide(null); load(); reloadTeams(); } catch (e) { setDecide((x) => ({ ...x, err: e.message })); }
  };
  const doNew = async () => {
    try { await call('POST', '/lessons', { teams: neu.teams, question: neu.question, answer: neu.answer }); setNeu(null); setStatus('proposed'); load(); reloadTeams(); } catch (e) { setNeu((x) => ({ ...x, err: e.message })); }
  };
  const ST = { proposed: t('xAiHub.stProposed'), approved: t('xAiHub.stApproved'), rejected: t('xAiHub.stRejected'), retired: t('xAiHub.stRetired') };
  const tv = useTableView(rows, {
    question: (l) => l.question, answer: (l) => l.answer, dept: (l) => deptsOf(l), proposed: (l) => l.proposed_at,
    status: (l) => ST[l.status] || l.status, decided: (l) => l.decided_at,
  }, (l) => [l.question, l.answer, deptsOf(l), l.proposed_by, l.decided_by, ST[l.status], l.decision_note, l.ask_answer].join(' '));
  return (
    <div>
      <p className="eu-sub">{t('xAiHub.lessonsSub')}</p>
      <div className="ah-filters">
        <div className="eu-chips">
          {['proposed', 'approved', 'rejected', 'retired', ''].map((s) => (
            <button key={s || 'all'} type="button" className={`eu-chip ${status === s ? 'on' : ''}`} onClick={() => setStatus(s)}>{s ? ST[s] : t('xAiHub.stAll')}</button>
          ))}
        </div>
        <select value={team} onChange={(e) => setTeam(e.target.value)} className="ah-select">
          <option value="">{t('xAiHub.allDepts')}</option><option value={GENERAL}>{t('xAiHub.general')}</option>{teams.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}
        </select>
        <SearchBox value={tv.qs} onChange={tv.setQs} shown={tv.view.length} total={(rows || []).length} />
        <button type="button" className="pa-btn primary" onClick={() => setNeu({ teams: team ? [team] : [], question: '', answer: '' })} data-ah-newlesson><Plus size={14} /> {t('xAiHub.newLesson')}</button>
        <label className="ah-origbox"><input type="checkbox" checked={orig} onChange={(e) => setOrig(e.target.checked)} data-ah-orig /> {t('xAiHub.showOriginal')}</label>
      </div>
      {trErr && <div className="eu-flash bad">{t('xAiHub.trFailed')}: {trErr}</div>}
      {err && <div className="eu-flash bad">{err}</div>}
      {!rows ? <p className="ah-muted">{t('xAiHub.loading')}</p> : !rows.length ? <p className="ah-muted">{t('xAiHub.noLessons')}</p> : (
        <div className="ah-tablewrap ah-listwrap">
          <table className="pa-table ah-table" data-ah-lessons>
            <thead><tr>
              {tv.th('question', t('xAiHub.colQuestion'))}{tv.th('answer', t('xAiHub.colAnswer'))}{tv.th('dept', t('xAiHub.colDept'))}
              {tv.th('proposed', t('xAiHub.colProposed'))}{tv.th('status', t('xAiHub.colStatus'))}{tv.th('decided', t('xAiHub.colDecided'))}
              <th>{t('xAiHub.colAction')}</th>
            </tr></thead>
            <tbody>
              {tv.view.map((l) => (
                <tr key={l.lesson_pk}>
                  <td className="ah-wrap" data-ah-lq><Tr text={l.question} tr={tr} orig={orig} />{l.ask_answer && <details className="ah-was"><summary>AI</summary><Tr text={l.ask_answer} tr={tr} orig={orig} /></details>}</td>
                  <td className="ah-wrap" data-ah-la><Tr text={l.answer} tr={tr} orig={orig} /></td>
                  <td className="ah-wrap" data-ah-ldepts>{isGen(l) ? <span className="ah-gen-tag">{t('xAiHub.general')}</span> : deptsOf(l)}</td>
                  <td>{l.proposed_by}<div className="ah-muted ah-stamp">{when(l.proposed_at)}</div></td>
                  <td><span className={`ah-st ah-st-${l.status}`}>{ST[l.status]}</span></td>
                  <td>{l.decided_by}<div className="ah-muted ah-stamp">{when(l.decided_at)}</div>{l.decision_note && <div className="ah-note"><Tr text={l.decision_note} tr={tr} orig={orig} /></div>}</td>
                  <td className="ah-actions">
                    {l.status === 'proposed' && mayDecide(l) && <button type="button" className="pa-btn ah-b-ok" onClick={() => setDecide({ l, d: 'approve', note: '' })} data-ah-approve>{t('xAiHub.actApprove')}</button>}
                    {!mayDecide(l) && l.status !== 'rejected' && l.status !== 'retired' && <span className="ah-muted ah-adminonly">{t('xAiHub.generalAdminOnly')}</span>}
                    {l.status === 'proposed' && <button type="button" className="pa-btn ah-b-off" onClick={() => setDecide({ l, d: 'reject', note: '' })}>{t('xAiHub.actReject')}</button>}
                    {l.status === 'approved' && mayDecide(l) && <button type="button" className="pa-btn ah-b-off" onClick={() => setDecide({ l, d: 'retire', note: '' })}>{t('xAiHub.actRetireLesson')}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {decide && (
        <Modal title={t('xAiHub.decideTitle', { d: verb[decide.d] })} onClose={() => setDecide(null)} foot={(
          <>
            <button type="button" className="pa-btn" onClick={() => setDecide(null)}>{t('xAiHub.cancel')}</button>
            <button type="button" className={`pa-btn primary ${decide.d === 'approve' ? 'ah-b-ok' : ''}`} onClick={doDecide} data-ah-decide>{verb[decide.d]}</button>
          </>
        )}>
          <p><b>{decide.l.question}</b></p>
          <pre className="ah-excerpt">{decide.l.answer}</pre>
          <label className="ah-field"><span>{t('xAiHub.decideNote')}</span><input value={decide.note} onChange={(e) => setDecide((x) => ({ ...x, note: e.target.value }))} /></label>
          {decide.err && <div className="eu-flash bad">{decide.err}</div>}
        </Modal>
      )}
      {neu && (
        <Modal title={t('xAiHub.newLesson')} onClose={() => setNeu(null)} foot={(
          <>
            <button type="button" className="pa-btn" onClick={() => setNeu(null)}>{t('xAiHub.cancel')}</button>
            <button type="button" className="pa-btn primary" disabled={!neu.question.trim() || !neu.answer.trim() || !neu.teams.length} onClick={doNew} data-ah-newsend>{t('xAiHub.fixSend')}</button>
          </>
        )}>
          <DeptChecks teams={teams} value={neu.teams} onChange={(v) => setNeu((x) => ({ ...x, teams: v }))} />
          <label className="ah-field"><span>{t('xAiHub.colQuestion')}</span><input value={neu.question} onChange={(e) => setNeu((x) => ({ ...x, question: e.target.value }))} /></label>
          <label className="ah-field"><span>{t('xAiHub.colAnswer')}</span><textarea rows={5} value={neu.answer} onChange={(e) => setNeu((x) => ({ ...x, answer: e.target.value }))} /></label>
          {neu.err && <div className="eu-flash bad">{neu.err}</div>}
        </Modal>
      )}
    </div>
  );
}

/* the change log in words (Max, 27/10, on the raw JSON under Details: "không có thân thiện với người dùng"): what happened,
   in the reader's language, and under Details a labelled list for that kind of change. Rows written before the server
   kept the title / question with every change fall back to the server's own English line. */
const quoted = (txt) => { const m = String(txt || '').match(/"([^"]+)"/); return m ? m[1] : ''; };
function auditWhat(r, t) {
  const d = r.detail || {};
  const file = d.kind === 'file';
  const q = d.question || quoted(r.summary);
  switch (r.action) {
    case 'doc.add': return d.title ? t(file ? 'xAiHub.wAddFile' : 'xAiHub.wAddInstr', { t: d.title }) + (d.active === false ? ` (${t('xAiHub.keptOff')})` : '') : r.summary;
    case 'doc.version': return d.title ? t(file ? 'xAiHub.wNewFile' : 'xAiHub.wEditInstr', { t: d.title, v: d.version }) + (d.active === false ? ` (${t('xAiHub.keptOff')})` : '') : r.summary;
    case 'doc.retire': return d.title ? t('xAiHub.wRetire', { t: d.title }) : r.summary;
    case 'doc.restore': return d.title ? t('xAiHub.wRestore', { t: d.title }) : r.summary;
    case 'doc.rollback': return d.title ? t('xAiHub.wRollback', { t: d.title, from: d.from, to: d.to }) : r.summary;
    case 'lesson.propose': return t(d.from === 'chat' ? 'xAiHub.wProposeChat' : 'xAiHub.wPropose', { q });
    case 'lesson.approve': return t('xAiHub.wApprove', { q });
    case 'lesson.reject': return t('xAiHub.wReject', { q });
    case 'lesson.retire': return t('xAiHub.wRetireLesson', { q });
    case 'ask.feedback': return t(d.value === 1 ? 'xAiHub.wUp' : d.value === -1 ? 'xAiHub.wDown' : 'xAiHub.wClear', { q });
    default: return r.summary;
  }
}
function AuditDetail({ r }) {
  const { t } = useTranslation();
  const d = r.detail || {};
  const ST = { proposed: t('xAiHub.stProposed'), approved: t('xAiHub.stApproved'), rejected: t('xAiHub.stRejected'), retired: t('xAiHub.stRetired') };
  const rows = [];
  const add = (k, v) => { if (v !== undefined && v !== null && v !== '') rows.push([t(`xAiHub.${k}`), v]); };
  if (r.action.startsWith('doc.')) {
    add('dType', d.kind === 'file' ? t('xAiHub.kindFile') : d.kind === 'instruction' ? t('xAiHub.kindInstr') : null);
    add('dTitle', d.title);
    if (d.version) add('dVersion', `v${d.version}`);
    if (r.action === 'doc.rollback') { add('dFrom', d.from && `v${d.from}`); add('dTo', d.to && `v${d.to}`); }
    add('dFile', d.fileName);
    if (d.bytes) add('dSize', kb(d.bytes));
    if (d.chars) add('dChars', Number(d.chars).toLocaleString());
    if (d.passages !== undefined) add('dPassages', d.passages);
  } else if (r.action.startsWith('lesson.')) {
    add('dQuestion', d.question);
    add('dAnswer', d.answer);
    if (d.before && d.after) add('dStatus', `${ST[d.before] || d.before} → ${ST[d.after] || d.after}`);
    add('dProposedBy', d.proposedBy);
    if (d.from === 'chat') add('dSaid', d.said);
  } else if (r.action === 'ask.feedback') {
    add('dQuestion', d.question || quoted(r.summary));
    add('dRating', d.value === 1 ? `👍 ${t('xAiHub.fbUp')}` : d.value === -1 ? `👎 ${t('xAiHub.fbDown')}` : '—');
  }
  add('dNote', d.note);
  const diff = r.action.startsWith('doc.') && d.kind === 'instruction' && typeof d.after === 'string';
  return (
    <div className="ah-auditdetail">
      {rows.length > 0 && <dl className="ah-dl">{rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>}
      {diff && (d.before
        ? <div className="ah-diff"><div><h5>{t('xAiHub.before')}</h5><pre>{d.before}</pre></div><div><h5>{t('xAiHub.after')}</h5><pre>{d.after}</pre></div></div>
        : <div className="ah-diff ah-diff1"><div><h5>{t('xAiHub.dText')}</h5><pre>{d.after}</pre></div></div>)}
    </div>
  );
}

/* ================================================================ Group chats, for the Portal Admins (Jay, task #1170) */
/* What Catchy did in the group chats (each question, its answer and the messages just before it) and the issues summary — gcc.js
   serves both to ai_hub editors only; the summary has no names and no quotes, by design */
const gccCall = async (method, url) => {
  const r = await fetch(`${BACKEND_URL}/api/gcc${url}`, { method, credentials: 'include' });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};
function GroupChats() {
  const { t, i18n } = useTranslation();
  const lang = ['vi', 'ko'].includes(String(i18n.language || '').slice(0, 2)) ? String(i18n.language).slice(0, 2) : 'en';
  const [dg, setDg] = useState(null);
  const [log, setLog] = useState(null);
  const [room, setRoom] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const loadDg = useCallback(() => gccCall('GET', '/admin/digests').then((r) => setDg(r.digests)).catch((e) => setErr(e.message)), []);
  useEffect(() => { loadDg(); }, [loadDg]);
  useEffect(() => { gccCall('GET', `/admin/catchy${room ? `?room=${room}` : ''}`).then(setLog).catch((e) => setErr(e.message)); }, [room]);
  const gv = useTableView(log?.rows || null, {
    when: (r) => r.created_at, room: (r) => r.room, who: (r) => (r.kind === 'side' ? '' : r.asker || ''), question: (r) => r.question || '',
  }, (r) => [r.room, r.asker, r.question, r.answer, ...(r.ctx || []).map((c) => c.body)].join(' '));
  const summarise = async () => {
    setBusy(true); setErr(''); setOk('');
    try {
      const r = await gccCall('POST', '/admin/digests');
      setOk(r.msgs ? t('xAiHub.gcSummarised', { n: r.topics, m: r.msgs }) : t('xAiHub.gcNothing'));
      await loadDg();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const itemText = (x) => (lang === 'vi' ? x.vi : lang === 'ko' ? x.ko : x.en) || x.en || x.vi;
  const card = (d) => (
    <div className="ah-gdigest" data-ah-gdigest={d.digest_pk}>
      <div className="ah-muted">{t('xAiHub.gcPeriod', { from: when(d.period_from), to: when(d.period_to), rooms: d.rooms, msgs: d.msgs })} · {d.made_by}</div>
      {d.topics.length ? d.topics.map((tp) => (
        <div key={tp.topic} className="ah-gtopic">
          <b>{tp.topic}</b>
          <ul>{tp.items.map((x, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}><span className={`ah-gkind k-${x.kind}`}>{t(`xAiHub.gcKind_${x.kind}`)}</span> {itemText(x)}</li>
          ))}</ul>
        </div>
      )) : <p className="ah-muted">—</p>}
    </div>
  );
  return (
    <div data-ah-groups>
      {err && <div className="eu-flash bad">{err}</div>}
      {ok && <div className="eu-flash ok">{ok}</div>}
      <section className="ah-gsec">
        <div className="ah-ghead">
          <h4><AiTag size={13} /> {t('xAiHub.gcDigestTitle')}</h4>
          <button type="button" className="pa-btn" disabled={busy} onClick={summarise} data-ah-gsummarise>{busy ? '…' : t('xAiHub.gcSummarise')}</button>
        </div>
        <p className="ah-muted">{t('xAiHub.gcDigestHint')}</p>
        {dg && (dg.length ? (
          <>
            {card(dg[0])}
            {dg.length > 1 && (
              <details className="ah-golder"><summary>{t('xAiHub.gcOlder')} ({dg.length - 1})</summary>
                {dg.slice(1).map((d) => <React.Fragment key={d.digest_pk}>{card(d)}</React.Fragment>)}
              </details>
            )}
          </>
        ) : <p className="ah-muted">{t('xAiHub.gcNoDigest')}</p>)}
      </section>
      <section className="ah-gsec">
        <h4>{t('xAiHub.gcLogTitle')}</h4>
        <p className="ah-muted">{t('xAiHub.gcLogHint')}</p>
        <div className="ah-filters">
          <select value={room} onChange={(e) => setRoom(e.target.value)} className="ah-select" data-ah-groom>
            <option value="">{t('xAiHub.gcAllRooms')}</option>{(log?.rooms || []).map((x) => <option key={x.pk} value={x.pk}>{x.name}</option>)}
          </select>
          <SearchBox value={gv.qs} onChange={gv.setQs} shown={gv.view.length} total={(log?.rows || []).length} />
        </div>
        {log && (log.rows.length ? (
          <div className="ah-tablewrap ah-listwrap">
            <table className="pa-table ah-table" data-ah-glog>
              <thead><tr>
                {gv.th('when', t('xAiHub.colWhen'))}{gv.th('room', t('xAiHub.colGroup'))}{gv.th('who', t('xAiHub.colAskedBy'))}{gv.th('question', t('xAiHub.colQuestion'))}
                <th>{t('xAiHub.colCatchyAnswer')}</th><th>{t('xAiHub.colContext')}</th>
              </tr></thead>
              <tbody>
                {gv.view.map((r) => (
                  <tr key={r.msg_pk} data-ah-glogrow={r.msg_pk}>
                    <td>{when(r.created_at)}</td><td>{r.room}</td>
                    <td>{r.kind === 'side' ? <em className="ah-muted">{t('xAiHub.gcSide')}</em> : r.asker}</td>
                    <td className="ah-wrap">{r.question || ''}</td>
                    <td className="ah-wrap ah-small"><details><summary>{String(r.answer || '').slice(0, 90)}{String(r.answer || '').length > 90 ? '…' : ''}</summary><Md text={r.answer || ''} cites={[]} onCite={() => {}} /></details></td>
                    <td className="ah-wrap ah-small">{(r.ctx || []).length ? (
                      <details><summary>{r.ctx.length}</summary>{r.ctx.map((c, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={i} className="ah-gctx"><b>{c.who}:</b> {c.body}</div>
                      ))}</details>
                    ) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="ah-muted">{t('xAiHub.gcNoLog')}</p>)}
      </section>
    </div>
  );
}

/* ================================================================ Activity */
function ActivityTab({ teams, admin }) {
  const { t } = useTranslation();
  const [view, setView] = useState('log');
  const [team, setTeam] = useState('');
  const [rows, setRows] = useState(null);
  const [asks, setAsks] = useState(null);
  const [orig, setOrig] = useState(false);
  const { tr, err: trErr } = useAiTranslate(useMemo(() => (asks?.asks || []).flatMap((a) => [a.question, a.answer, a.feedback_note]), [asks]));
  const [fb, setFb] = useState('');
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);
  const nameOf = (k) => (k ? teams.find((x) => x.key === k)?.name || k : '');
  /* the log narrowed by who did it and by the kind of change, then searched and sorted like the other lists */
  const [who, setWho] = useState('');
  const [kind, setKind] = useState('');
  const logRows = useMemo(() => (rows || []).filter((r) => (!who || r.user_name === who) && (!kind || r.action.startsWith(kind))), [rows, who, kind]);
  const people = useMemo(() => [...new Set((rows || []).map((r) => r.user_name).filter(Boolean))].sort(), [rows]);
  const lv = useTableView(logRows, { when: (r) => r.at, who: (r) => r.user_name, dept: (r) => nameOf(r.team_key), what: (r) => auditWhat(r, t) },
    (r) => [when(r.at), r.user_name, nameOf(r.team_key), auditWhat(r, t)].join(' '));
  const qv = useTableView(asks?.asks || null, {
    when: (a) => a.created_at, who: (a) => a.user_name, dept: (a) => nameOf(a.scope_team), question: (a) => a.question,
    rating: (a) => a.feedback, cost: (a) => (a.usd == null ? null : Number(a.usd)),
  }, (a) => [when(a.created_at), a.user_name, nameOf(a.scope_team), a.question, a.answer, a.feedback_note].join(' '));
  useEffect(() => {
    setErr('');
    if (view === 'log') call('GET', `/audit${team ? `?team=${team}` : ''}`).then((r) => setRows(r.rows)).catch((e) => setErr(e.message));
    if (view === 'questions') call('GET', `/asks?${new URLSearchParams({ ...(team ? { team } : {}), ...(fb ? { feedback: fb } : {}) })}`).then((r) => setAsks(r)).catch((e) => setErr(e.message));
  }, [view, team, fb]);
  return (
    <div>
      <div className="ah-filters">
        <div className="eu-chips">
          {/* Group chats: Portal Admins only (Jay #1170) */}
          {['log', 'questions', ...(admin ? ['groups'] : [])].map((v) => <button key={v} type="button" className={`eu-chip ${view === v ? 'on' : ''}`} onClick={() => setView(v)} data-ah-view={v}>{t(`xAiHub.${v}`)}</button>)}
        </div>
        {view !== 'groups' && (
        <select value={team} onChange={(e) => setTeam(e.target.value)} className="ah-select">
          <option value="">{t('xAiHub.allDepts')}</option>{teams.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}
        </select>
        )}
        {view === 'log' && (
          <>
            <select value={who} onChange={(e) => setWho(e.target.value)} className="ah-select" data-ah-who>
              <option value="">{t('xAiHub.fWhoAll')}</option>{people.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="ah-select" data-ah-kind>
              <option value="">{t('xAiHub.fKindAll')}</option>
              <option value="doc.">{t('xAiHub.fKindDocs')}</option>
              <option value="lesson.">{t('xAiHub.fKindLessons')}</option>
              <option value="ask.">{t('xAiHub.fKindRatings')}</option>
            </select>
            <SearchBox value={lv.qs} onChange={lv.setQs} shown={lv.view.length} total={(rows || []).length} />
          </>
        )}
        {view === 'questions' && (
          <>
            <select value={fb} onChange={(e) => setFb(e.target.value)} className="ah-select"><option value="">{t('xAiHub.fbAll')}</option><option value="1">{t('xAiHub.fbUp')}</option><option value="-1">{t('xAiHub.fbDown')}</option></select>
            <SearchBox value={qv.qs} onChange={qv.setQs} shown={qv.view.length} total={(asks?.asks || []).length} />
            <label className="ah-origbox"><input type="checkbox" checked={orig} onChange={(e) => setOrig(e.target.checked)} /> {t('xAiHub.showOriginal')}</label>
          </>
        )}
      </div>
      {err && <div className="eu-flash bad">{err}</div>}
      {view === 'groups' && <GroupChats />}
      {view === 'questions' && trErr && <div className="eu-flash bad">{t('xAiHub.trFailed')}: {trErr}</div>}
      {view === 'log' && rows && (rows.length ? (
        <div className="ah-tablewrap ah-listwrap">
          <table className="pa-table ah-table" data-ah-audit>
            <thead><tr>
              {lv.th('when', t('xAiHub.colWhen'))}{lv.th('who', t('xAiHub.colWho'))}{lv.th('dept', t('xAiHub.colDept'))}{lv.th('what', t('xAiHub.colWhat'))}
              <th>{t('xAiHub.details')}</th>
            </tr></thead>
            <tbody>
              {lv.view.map((r) => (
                <React.Fragment key={r.audit_pk}>
                  <tr>
                    <td>{when(r.at)}</td><td>{r.user_name}</td><td>{nameOf(r.team_key)}</td><td className="ah-wrap">{auditWhat(r, t)}</td>
                    <td>{r.detail && <button type="button" className="pa-btn ah-b-view" onClick={() => setOpen(open === r.audit_pk ? null : r.audit_pk)}>{t('xAiHub.details')}</button>}</td>
                  </tr>
                  {open === r.audit_pk && (
                    <tr className="ah-detail"><td colSpan={5}>
                      <AuditDetail r={r} />
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="ah-muted">{t('xAiHub.noRows')}</p>)}
      {view === 'questions' && asks && (asks.asks.length ? (
        <div className="ah-tablewrap ah-listwrap">
          <table className="pa-table ah-table" data-ah-asks>
            <thead><tr>
              {qv.th('when', t('xAiHub.colWhen'))}{qv.th('who', t('xAiHub.colWho'))}{qv.th('dept', t('xAiHub.colDept'))}{qv.th('question', t('xAiHub.colQuestion'))}
              <th>AI</th>{qv.th('rating', '👍/👎')}{qv.th('cost', t('xAiHub.colCost'))}
            </tr></thead>
            <tbody>
              {qv.view.map((a) => (
                <tr key={a.ask_pk}>
                  <td>{when(a.created_at)}</td><td>{a.user_name}</td><td>{nameOf(a.scope_team) || t('xAiHub.allDepts')}</td>
                  <td className="ah-wrap"><Tr text={a.question} tr={tr} orig={orig} /></td>
                  <td className="ah-wrap ah-small">{a.ok ? (() => {
                    const ans = !orig && tr(a.answer);
                    return <details><summary>{ans && <AiTag size={11} className="ah-trmark" />}{String(ans || a.answer || '').slice(0, 90)}…</summary><Md text={ans || a.answer} cites={a.sources || []} onCite={() => {}} /></details>;
                  })() : <span className="ah-err">{a.error}</span>}</td>
                  <td>{a.feedback === 1 ? '👍' : a.feedback === -1 ? '👎' : ''}{a.feedback_note && <div className="ah-note"><Tr text={a.feedback_note} tr={tr} orig={orig} /></div>}</td>
                  <td>{a.usd != null ? usd(a.usd) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="ah-muted">{t('xAiHub.noRows')}</p>)}
    </div>
  );
}

/* ================================================================ the page */
export default function AiHub() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const tabQ = new URLSearchParams(location.search).get('tab');
  const [teams, setTeams] = useState([]);
  const [admin, setAdmin] = useState(false);
  const [err, setErr] = useState('');
  const [focus, setFocus] = useState(null);
  const [loaded, setLoaded] = useState(false);
  /* TWO PAGES SINCE 30/10 (Jay, task #99: "create a Settings page under Catchy Chat and move Knowledge Library, Lessons
     and Admin into it (admin only)"). ?tab=ask is the chat alone; the other three tabs are Settings, for an ai_hub editor
     or a department's manager — the same people the menu draws the Settings row for. Anybody else asking for them gets
     the chat, except a source opened from an answer (focus): that one document is still theirs to read. */
  const isManager = admin || teams.some((x) => x.canManage);
  const settingsTabs = TABS.filter((x) => x !== 'ask');
  /* Knowledge Library is no longer a tab (Max 03/11: "bỏ luôn vì đã upload bên Library module" — both of its files moved to E-Docs
     on 30/10); it opens only for a source picked from an answer */
  const listedTabs = settingsTabs.filter((x) => x !== 'knowledge');
  const tab = tabQ === 'ask' || !settingsTabs.includes(tabQ) ? 'ask'
    : (!loaded || isManager || (tabQ === 'knowledge' && focus) ? tabQ : 'ask');
  const settings = tab !== 'ask';
  const tabs = settings && isManager ? listedTabs : [];
  const setTab = (x) => navigate(`/ai-hub?tab=${x}`, { replace: true });
  const loadTeams = useCallback(() => call('GET', '/teams').then((r) => { setTeams(r.teams); setAdmin(!!r.admin); setLoaded(true); }).catch((e) => setErr(e.message)), []);
  useEffect(() => { loadTeams(); }, [loadTeams]);
  const waiting = useMemo(() => teams.reduce((a, x) => a + (x.proposed || 0), 0), [teams]);
  const openDoc = (team, docPk) => { setFocus({ team, docPk, at: Date.now() }); setTab('knowledge'); };
  /* the title, the line under it and the tabs stay under the portal's header while the page scrolls (Max, 27/10:
     "freeze/stick phần này lại nhé"); their height — it changes when the line wraps — is measured into --ah-head so the
     department list can stick just below them */
  const headRef = useRef(null);
  const pageRef = useRef(null);
  useEffect(() => {
    const h = headRef.current; const pg = pageRef.current;
    if (!h || !pg) return undefined;
    const set = () => {
      pg.style.setProperty('--ah-head', `${Math.round(h.getBoundingClientRect().height)}px`);
      /* where the portal's fixed header ends, seen from the box that scrolls the page */
      const hd = document.querySelector('header');
      let sc = pg.parentElement;
      while (sc && !/(auto|scroll)/.test(getComputedStyle(sc).overflowY)) sc = sc.parentElement;
      if (hd) pg.style.setProperty('--ah-top', `${Math.max(0, Math.round(hd.getBoundingClientRect().bottom - (sc ? sc.getBoundingClientRect().top : 0)))}px`);
    };
    set();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(set) : null;
    if (ro) { ro.observe(h); const hd = document.querySelector('header'); if (hd) ro.observe(hd); }
    return () => { if (ro) ro.disconnect(); };
  }, []);
  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main ah-page" ref={pageRef}>
        <div className="ah-head" ref={headRef} data-ah-head>
        <div className="mh-titlerow">
          <h2 className="pa-title">{settings ? `${t('xAiHub.title')} · ${t('xAiHub.navSettings')}` : t('xAiHub.title')}</h2>
          <ModuleHelp steps={TOUR} tourKey="aiHub" ready={loaded && teams.length > 0} auto={!settings} onBeforeTour={() => { if (settings) setTab('ask'); }} />
        </div>
        {!settings && <p className="eu-sub ah-lead">{t('xAiHub.sub')}</p>}
        {settings && loaded && !isManager && (
          <button type="button" className="pa-btn" onClick={() => setTab('ask')} data-ah-back>{t('xAiHub.backToChat')}</button>
        )}
        {tabs.length > 0 && <div className="ah-tabs view-tabs" role="tablist">
          {tabs.map((x) => (
            <button key={x} type="button" role="tab" aria-selected={tab === x} className={`ah-tab ${tab === x ? 'on' : ''}`} onClick={() => setTab(x)} data-ah-tab={x}>
              <TabIcon k={x} />{t(`xAiHub.tab${x[0].toUpperCase()}${x.slice(1)}`)}{x === 'lessons' && waiting > 0 && <span className="ah-badge">{waiting}</span>}
            </button>
          ))}
        </div>}
        </div>
        {err && <div className="eu-flash bad">{err}</div>}
        {teams.length > 0 && (
          <>
            {tab === 'ask' && <AskTab teams={teams} onOpenDoc={openDoc} />}
            {tab === 'knowledge' && <KnowledgeTab teams={teams} reloadTeams={loadTeams} focus={focus} />}
            {tab === 'lessons' && <LessonsTab teams={teams} reloadTeams={loadTeams} admin={admin} />}
            {tab === 'activity' && <ActivityTab teams={admin ? teams : teams.filter((x) => x.canManage)} admin={admin} />}
          </>
        )}
      </div>
    </div>
  );
}
